import React, { useRef, useCallback, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Refresh as RefreshIcon,
  Comment as CommentIcon,
  SwapHoriz as StageIcon,
  SmartToy as SystemIcon,
  ChatBubbleOutline as ChatIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  UploadFile as UploadFileIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon,
  Gavel as GavelIcon,
  Build as BuildIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useProjectActivityFeed } from "../../../../hooks/api/activityFeedApiHooks";
import { useUpdateProjectComment, useDeleteProjectComment } from "../../../../hooks/api/projectCommentsApiHooks";
import useAuth from "../../../../hooks/useAuth";
import CommentForm from "../../project/comments/CommentForm";
import RichCommentContent from "../../project/comments/RichCommentContent";

const FILTER_STORAGE_KEY = "activityFeedFilters";

const FILTER_OPTIONS = {
  all: { label: "Todos", icon: null },
  comment: { label: "Comentários", icon: <ChatIcon sx={{ fontSize: 18 }} /> },
  activity: { label: "Atividades", icon: <StageIcon sx={{ fontSize: 18 }} /> },
};

function getStoredFilter() {
  try {
    return localStorage.getItem(FILTER_STORAGE_KEY) || "all";
  } catch {
    return "all";
  }
}

function setStoredFilter(filter) {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, filter);
  } catch {
    // localStorage indisponível
  }
}

function formatItemDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Agrupamento por data
function getDateGroup(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  if (date >= today) return "HOJE";
  if (date >= yesterday) return "ONTEM";
  if (date >= weekAgo) return "ESTA SEMANA";
  if (date >= monthStart) return "ESTE MÊS";
  return "MAIS ANTIGOS";
}

function groupByDate(items) {
  const groups = [];
  let currentGroup = null;
  for (const item of items) {
    const group = getDateGroup(item.date);
    if (!currentGroup || currentGroup.label !== group) {
      currentGroup = { label: group, items: [] };
      groups.push(currentGroup);
    }
    currentGroup.items.push(item);
  }
  return groups;
}

const SYSTEM_ACTION_CONFIG = {
  stage_change: { icon: <StageIcon sx={{ fontSize: 16 }} />, label: "mudança de etapa", color: "warning" },
  providence_change: { icon: <BuildIcon sx={{ fontSize: 16 }} />, label: "providência", color: "info" },
  project_created: { icon: <AddIcon sx={{ fontSize: 16 }} />, label: "criação", color: "success" },
  project_billed: { icon: <ReceiptIcon sx={{ fontSize: 16 }} />, label: "cobrança", color: "error" },
  project_generated: { icon: <DescriptionIcon sx={{ fontSize: 16 }} />, label: "projeto gerado", color: "info" },
  document_uploaded: { icon: <UploadFileIcon sx={{ fontSize: 16 }} />, label: "documento", color: "info" },
  payment_registered: { icon: <PaymentIcon sx={{ fontSize: 16 }} />, label: "pagamento", color: "success" },
  checklist_completed: { icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, label: "checklist", color: "success" },
  checklist_item_added: { icon: <AddIcon sx={{ fontSize: 16 }} />, label: "checklist", color: "info" },
  checklist_item_checked: { icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, label: "checklist", color: "success" },
  checklist_item_unchecked: { icon: <CheckCircleIcon sx={{ fontSize: 16 }} />, label: "checklist", color: "warning" },
  checklist_item_deleted: { icon: <DeleteIcon sx={{ fontSize: 16 }} />, label: "checklist", color: "error" },
  assignment_changed: { icon: <PersonAddIcon sx={{ fontSize: 16 }} />, label: "atribuição", color: "info" },
  contract_created: { icon: <GavelIcon sx={{ fontSize: 16 }} />, label: "contrato", color: "info" },
};

function getSystemConfig(actionType) {
  return SYSTEM_ACTION_CONFIG[actionType] || { icon: <SettingsIcon sx={{ fontSize: 16 }} />, label: "sistema", color: "default" };
}

const EDIT_WINDOW_MS = 5 * 60 * 1000; // 5 minutos

function canEdit(item, currentUserId) {
  if (!currentUserId) return false;
  if (item.metadata?.isSystem) return false;
  if (item.metadata?.userId !== currentUserId) return false;
  const elapsed = Date.now() - new Date(item.date).getTime();
  return elapsed <= EDIT_WINDOW_MS;
}

function canDelete(item, currentUserId, isAdmin) {
  if (!currentUserId) return false;
  if (item.metadata?.isSystem) return false;
  if (isAdmin) return true;
  if (item.metadata?.userId !== currentUserId) return false;
  const elapsed = Date.now() - new Date(item.date).getTime();
  return elapsed <= EDIT_WINDOW_MS;
}

// ─── Atividade de sistema (timeline compacta) ────────────────────────
function SystemActivityItem({ item }) {
  const theme = useTheme();
  const actionType = item.metadata?.action_type || (item.type === "stage_change" ? "stage_change" : undefined);
  const cfg = getSystemConfig(actionType);
  const colorKey = cfg.color && cfg.color !== "default" ? cfg.color : null;
  const dotColor = colorKey ? (theme.palette[colorKey]?.main ?? theme.palette.grey[400]) : theme.palette.grey[400];

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1, pl: 0.5 }}>
      {/* Dot / icon */}
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          bgcolor: dotColor + "20",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: 0.25,
          color: dotColor,
        }}
      >
        {cfg.icon || <SettingsIcon sx={{ fontSize: 14 }} />}
      </Box>
      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
            {item.author || "Sistema"}
          </Typography>
          {" "}
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              bgcolor: colorKey ? theme.palette[colorKey]?.main + "18" : undefined,
              color: colorKey ? theme.palette[colorKey]?.dark : undefined,
              verticalAlign: "middle",
            }}
          />
          {" · "}
          <Typography component="span" variant="caption" color="text.secondary">
            {formatItemDate(item.date)}
          </Typography>
        </Typography>
        {item.content && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {item.content}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Comentário de usuário (chat bubble) ─────────────────────────────
function UserCommentItem({ item, currentUserId, isAdmin, onEdit, onDelete }) {
  const theme = useTheme();
  const showEdit = canEdit(item, currentUserId);
  const showDelete = canDelete(item, currentUserId, isAdmin);

  return (
    <Box sx={{ display: "flex", gap: 1.5, py: 1.5, "&:hover .comment-actions": { opacity: 1 } }}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: theme.palette.primary.main,
          fontSize: "0.75rem",
          fontWeight: 600,
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {getInitials(item.author)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {item.author || "Usuário"}
          </Typography>
          {item.metadata?.isEdited && (
            <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "text.disabled", fontStyle: "italic" }}>
              editado
            </Typography>
          )}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            {(showEdit || showDelete) && (
              <Box className="comment-actions" sx={{ opacity: 0, transition: "opacity 0.2s", display: "flex", gap: 0.25 }}>
                {showEdit && (
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => onEdit(item)} sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "primary.main" } }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {showDelete && (
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => onDelete(item)} sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "error.main" } }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              {formatItemDate(item.date)}
            </Typography>
          </Box>
        </Box>
        {/* Bubble */}
        <Box
          sx={{
            mt: 0.5,
            bgcolor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px 12px 12px 12px",
            px: 1.5,
            py: 1,
          }}
        >
          <RichCommentContent content={item.content} />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Dispatch: decide qual estilo renderizar ─────────────────────────
function ActivityItem({ item, currentUserId, isAdmin, onEdit, onDelete }) {
  const isSystem = item.metadata?.isSystem || item.type === "stage_change";

  if (isSystem) {
    return <SystemActivityItem item={item} />;
  }

  return (
    <UserCommentItem
      item={item}
      currentUserId={currentUserId}
      isAdmin={isAdmin}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
}

export default function ActivityFeed({ projectId, projectNumber }) {
  const theme = useTheme();
  const { auth } = useAuth();
  const [activeFilter, setActiveFilter] = useState(getStoredFilter);
  const [editingComment, setEditingComment] = useState(null);
  const { deleteComment } = useDeleteProjectComment();
  const {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useProjectActivityFeed(projectId);

  const filteredItems = useMemo(() => {
    if (activeFilter === "all") return items;
    if (activeFilter === "activity") {
      return items.filter((i) => i.metadata?.isSystem || i.type === "stage_change");
    }
    if (activeFilter === "comment") {
      return items.filter((i) => !i.metadata?.isSystem && i.type !== "stage_change");
    }
    return items;
  }, [items, activeFilter]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setStoredFilter(filter);
  };

  const observerRef = useRef();
  const lastItemRef = useCallback(
    (node) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  const [spinning, setSpinning] = React.useState(false);
  const handleRefresh = () => {
    setSpinning(true);
    refetch();
    setTimeout(() => setSpinning(false), 1000);
  };

  const handleEdit = (item) => {
    setEditingComment({
      commentId: item.metadata?.commentId,
      content: item.content,
    });
  };

  const handleDelete = (item) => {
    if (window.confirm("Excluir este comentário?")) {
      deleteComment(item.metadata?.commentId);
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Atividades
          </Typography>
          {filteredItems.length > 0 && (
            <Chip
              label={filteredItems.length}
              size="small"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          {Object.entries(FILTER_OPTIONS).map(([key, opt]) => (
            <Tooltip key={key} title={opt.label}>
              <IconButton
                size="small"
                onClick={() => handleFilterChange(key)}
                sx={{
                  borderRadius: "8px",
                  bgcolor: activeFilter === key ? theme.palette.primary.main + "15" : "transparent",
                  color: activeFilter === key ? theme.palette.primary.main : theme.palette.text.secondary,
                  border: activeFilter === key ? `1px solid ${theme.palette.primary.main}30` : "1px solid transparent",
                  px: opt.icon ? 0.75 : 1,
                  fontSize: "0.7rem",
                  "&:hover": {
                    bgcolor: theme.palette.primary.main + "10",
                  },
                }}
              >
                {opt.icon || (
                  <Typography variant="caption" fontWeight={activeFilter === key ? 600 : 400} sx={{ fontSize: "0.7rem" }}>
                    Todos
                  </Typography>
                )}
              </IconButton>
            </Tooltip>
          ))}
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={handleRefresh} disabled={isLoading} sx={{ ml: 0.5 }}>
              <RefreshIcon
                fontSize="small"
                sx={{
                  animation: (isLoading || spinning) ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Comment Form */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <CommentForm
          projectId={projectId}
          projectNumber={projectNumber}
          onCommentAdded={handleRefresh}
          editingComment={editingComment}
          onCancelEdit={handleCancelEdit}
        />
      </Box>

      {/* Feed List */}
      <Box sx={{ px: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {activeFilter === "all" ? "Nenhuma atividade registrada" : "Nenhum resultado para este filtro"}
            </Typography>
          </Box>
        ) : (
          groupByDate(filteredItems).map((group, groupIdx) => (
            <Box key={group.label}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  color: theme.palette.text.secondary,
                  fontSize: "0.65rem",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  pt: 1.5,
                  pb: 0.5,
                  px: 0.5,
                  borderTop: groupIdx > 0 ? `1px solid ${theme.palette.divider}` : "none",
                }}
              >
                {group.label}
              </Typography>
              {group.items.map((item, index) => {
                const globalIdx = filteredItems.indexOf(item);
                return (
                  <Box
                    key={item.id}
                    ref={globalIdx === filteredItems.length - 1 ? lastItemRef : undefined}
                  >
                    <ActivityItem item={item} currentUserId={auth?.id} isAdmin={auth?.roles?.includes(2019)} onEdit={handleEdit} onDelete={handleDelete} />
                  </Box>
                );
              })}
            </Box>
          ))
        )}
        {isFetchingNextPage && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
